import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  TaskFollowerRequest, TaskFollowerResponse,
  TaskDependencyRequest, TaskDependencyResponse,
  TaskContactRequest, TaskContactResponse,
  TaskStatusHistoryResponse,
  TaskAttributeCustomRequest, TaskAttributeCustomResponse, TaskAttributeCustomBulkRequest
} from '../models/task.model';

/**
 * Sub-recursos del módulo de tareas: followers, dependencies, contacts, status-history, custom attributes.
 */
@Injectable({ providedIn: 'root' })
export class TaskSubResourcesApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) {}

  // ===== Followers =====
  listFollowers(taskId: number): Observable<TaskFollowerResponse[]> {
    return this.http.get<TaskFollowerResponse[]>(`${this.apiUrl}/tasks/${taskId}/followers`);
  }
  addFollower(taskId: number, req: TaskFollowerRequest): Observable<TaskFollowerResponse> {
    return this.http.post<TaskFollowerResponse>(`${this.apiUrl}/tasks/${taskId}/followers`, req);
  }
  removeFollower(taskId: number, userId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tasks/${taskId}/followers/${userId}`);
  }
  followSelf(taskId: number): Observable<TaskFollowerResponse> {
    return this.http.post<TaskFollowerResponse>(`${this.apiUrl}/tasks/${taskId}/followers/self`, {});
  }
  unfollowSelf(taskId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tasks/${taskId}/followers/self`);
  }

  // ===== Dependencies =====
  listDependencies(taskId: number): Observable<TaskDependencyResponse[]> {
    return this.http.get<TaskDependencyResponse[]>(`${this.apiUrl}/tasks/${taskId}/dependencies`);
  }
  addDependency(taskId: number, req: TaskDependencyRequest): Observable<TaskDependencyResponse> {
    return this.http.post<TaskDependencyResponse>(`${this.apiUrl}/tasks/${taskId}/dependencies`, req);
  }
  removeDependency(taskId: number, blockerId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tasks/${taskId}/dependencies/${blockerId}`);
  }
  listBlocking(taskId: number): Observable<TaskDependencyResponse[]> {
    return this.http.get<TaskDependencyResponse[]>(`${this.apiUrl}/tasks/${taskId}/dependencies/blocking`);
  }

  // ===== Contacts =====
  listContacts(taskId: number): Observable<TaskContactResponse[]> {
    return this.http.get<TaskContactResponse[]>(`${this.apiUrl}/tasks/${taskId}/contacts`);
  }
  addContact(taskId: number, req: TaskContactRequest): Observable<TaskContactResponse> {
    return this.http.post<TaskContactResponse>(`${this.apiUrl}/tasks/${taskId}/contacts`, req);
  }
  removeContact(taskId: number, contactId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tasks/${taskId}/contacts/${contactId}`);
  }

  // ===== Status history =====
  listStatusHistory(taskId: number): Observable<TaskStatusHistoryResponse[]> {
    return this.http.get<TaskStatusHistoryResponse[]>(`${this.apiUrl}/tasks/${taskId}/status-history`);
  }

  // ===== Custom attributes (EAV) =====
  listAttributes(taskId: number): Observable<TaskAttributeCustomResponse[]> {
    return this.http.get<TaskAttributeCustomResponse[]>(`${this.apiUrl}/tasks/${taskId}/attributes`);
  }
  upsertAttribute(taskId: number, definitionId: number, req: TaskAttributeCustomRequest): Observable<TaskAttributeCustomResponse> {
    return this.http.put<TaskAttributeCustomResponse>(
      `${this.apiUrl}/tasks/${taskId}/attributes/${definitionId}`,
      req
    );
  }
  bulkUpsertAttributes(taskId: number, req: TaskAttributeCustomBulkRequest): Observable<TaskAttributeCustomResponse[]> {
    return this.http.put<TaskAttributeCustomResponse[]>(
      `${this.apiUrl}/tasks/${taskId}/attributes`,
      req
    );
  }
  deleteAttribute(taskId: number, definitionId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tasks/${taskId}/attributes/${definitionId}`);
  }
}
